import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Clock, CheckCircle, XCircle, RefreshCw, ArrowLeft, FileText, User, Building2, Calendar, MessageSquare, Download, Eye, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from '../../lib/safeToast';

const STATUS_LABELS = {
  in_review: 'In Review',
  info_requested: 'Info Requested',
  co_recommended_approve: 'Compliance Approved',
  co_recommended_reject: 'Compliance Rejected',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

const STATUS_BADGE = {
  in_review: 'bg-blue-50 text-blue-700 border-blue-200',
  info_requested: 'bg-amber-50 text-amber-700 border-amber-200',
  co_recommended_approve: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  co_recommended_reject: 'bg-red-50 text-red-700 border-red-200',
  pending: 'bg-slate-50 text-slate-600 border-slate-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

const DOC_LABELS = {
  asic_registration: 'ASIC Registration',
  business_license: 'Business License',
  home_address: 'Proof of Address',
  bank_statement: 'Bank Statement',
  id_document: 'ID Document',
  tax_registration: 'Tax Registration',
  other: 'Other Document',
};
function getDocLabel(doc) {
  const type = doc?.type || doc?.doc_type || '';
  if (type.startsWith('other_')) {
    const n = parseInt(type.replace('other_', ''), 10);
    return Number.isFinite(n) ? `Other document (${n + 1})` : type;
  }
  if (type.startsWith('info_request_attachment_')) {
    const n = parseInt(type.replace('info_request_attachment_', ''), 10);
    return Number.isFinite(n) ? `Info request attachment (${n + 1})` : 'Info request attachment';
  }
  return DOC_LABELS[type] || type || doc?.file_name || 'Document';
}

const REVIEW_ACTION_LABELS = {
  co_review: 'CO Review',
  co_recommended_approve: 'CO Recommended Approval',
  co_recommended_reject: 'CO Recommended Rejection',
  document_accepted: 'Document Accepted',
  document_update_requested: 'Document Update Requested',
  info_request: 'Information Requested',
  approved: 'KYC Approved',
  rejected: 'KYC Rejected',
};

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

function base64ToBlobUrl(dataUrl) {
  try {
    const [meta, b64] = dataUrl.split(',');
    const mime = meta.match(/:(.*?);/)?.[1] || 'application/octet-stream';
    const bytes = atob(b64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    return URL.createObjectURL(new Blob([arr], { type: mime }));
  } catch { return null; }
}

function DocPreview({ doc }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!doc?.data_url) return;
    const url = base64ToBlobUrl(doc.data_url);
    if (url) setBlobUrl(url);
    else setError(true);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [doc?.data_url]);

  if (!doc?.data_url) return <p className="text-xs text-slate-400 italic">No file uploaded</p>;

  const isPdf = doc.data_url.startsWith('data:application/pdf') || doc.file_name?.endsWith('.pdf');

  if (error || !blobUrl) {
    return (
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <FileText className="w-4 h-4" />
        <span>{doc.file_name || 'Document'}</span>
        <a href={doc.data_url} download={doc.file_name || 'document'} className="text-[#0B6445] hover:underline ml-1"><Download className="w-3 h-3 inline" /> Download</a>
      </div>
    );
  }

  if (isPdf) {
    return (
      <div className="space-y-1">
        <p className="text-xs font-medium text-slate-600">{doc.file_name || 'PDF Document'}</p>
        <iframe src={blobUrl} className="w-full h-48 rounded border border-slate-200" title={doc.file_name || 'PDF'} />
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-slate-600">{doc.file_name || 'Image Document'}</p>
      <img src={blobUrl} alt={doc.file_name || 'Document'} className="max-h-48 rounded border border-slate-200 object-contain" onError={() => setError(true)} />
    </div>
  );
}

function CollapsibleSection({ icon: Icon, title, count, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="border-[#DCE4E0] shadow-sm">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50/50 transition-colors rounded-t-lg"
      >
        <span className="text-sm font-semibold text-[#073B2E] flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4" />}
          {title} {count != null && <span className="text-xs font-normal text-slate-400">({count})</span>}
        </span>
        {open
          ? <ChevronDown className="w-4 h-4 text-slate-400" />
          : <ChevronRight className="w-4 h-4 text-slate-400" />
        }
      </button>
      {open && (
        <CardContent className="pt-0 pb-5 px-6 border-t border-slate-100">
          {children}
        </CardContent>
      )}
    </Card>
  );
}

function MerchantDetail({ merchant, api, currentUser, onBack, onActionComplete }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionType, setActionType] = useState(null);
  const [suComments, setSuComments] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [expandedInfoReqs, setExpandedInfoReqs] = useState({});

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/backoffice/users/${merchant.id}/kyb-details`);
      setDetail(res.data);
    } catch {
      toast.error('Failed to load merchant details');
    } finally { setLoading(false); }
  }, [api, merchant.id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleAction = async () => {
    if (!suComments.trim()) { toast.error('Comments are mandatory'); return; }
    setActionLoading(true);
    try {
      if (actionType === 'approve') {
        await api.post(`/backoffice/kyc/${merchant.id}/approve`, { comments: suComments.trim() });
        toast.success(`${merchant.name || merchant.email} approved`);
      } else {
        await api.post(`/backoffice/kyc/${merchant.id}/reject`, { comments: suComments.trim() });
        toast.success(`${merchant.name || merchant.email} rejected`);
      }
      setActionType(null);
      setSuComments('');
      onActionComplete();
    } catch { toast.error('Action failed'); } finally { setActionLoading(false); }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <div className="spinner w-8 h-8" />
        <p className="text-sm text-slate-500">Loading merchant details…</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Could not load details.</p>
        <Button variant="outline" onClick={onBack} className="mt-4">Go back</Button>
      </div>
    );
  }

  const m = detail.merchant || {};
  const ob = detail.onboarding || {};
  const company = detail.company_information || {};
  const reviews = detail.review_history || [];
  const infoReqs = detail.information_requests || [];
  const docReviews = detail.document_reviews || {};
  const docs = ob.supporting_documents || [];
  const biz = ob.business || {};

  const isSU = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';
  const coHasRecommended = m.kyc_status === 'co_recommended_approve' || m.kyc_status === 'co_recommended_reject';
  const showApproveReject = isSU || coHasRecommended;

  return (
    <div className="flex flex-col gap-10">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="text-[#0B6445] hover:bg-[#E6F7F3] gap-2 -ml-2">
          <ArrowLeft className="w-4 h-4" /> Back to list
        </Button>
        {showApproveReject && (
          <div className="flex gap-3">
            <Button size="sm" onClick={() => { setActionType('approve'); setSuComments(''); }}
              className="bg-[#0B6445] hover:bg-[#095b3d] text-white">
              <CheckCircle className="w-4 h-4 mr-1" /> Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setActionType('reject'); setSuComments(''); }}
              className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700">
              <XCircle className="w-4 h-4 mr-1" /> Reject
            </Button>
          </div>
        )}
      </div>

      {/* ── Section 1: Merchant Header ── */}
      <Card className="border-[#DCE4E0] shadow-sm">
        <CardContent className="py-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#E6F7F3] flex items-center justify-center text-[#0B6445] font-bold text-xl">
                {(m.name || m.email || '?').charAt(0).toUpperCase()}
              </div>
              <div>
                <h2 className="text-xl font-bold text-[#073B2E]">{m.name || '—'}</h2>
                <p className="text-sm text-slate-500">{m.email}</p>
                {m.company_name && <p className="text-xs text-slate-400 mt-0.5">{m.company_name}</p>}
              </div>
            </div>
            <Badge variant="outline" className={`text-sm px-3 py-1 ${STATUS_BADGE[m.kyc_status] || STATUS_BADGE.pending}`}>
              {m.kyc_status === 'co_recommended_approve' && <CheckCircle className="w-3.5 h-3.5 mr-1" />}
              {m.kyc_status === 'co_recommended_reject' && <XCircle className="w-3.5 h-3.5 mr-1" />}
              {STATUS_LABELS[m.kyc_status] || m.kyc_status}
            </Badge>
          </div>
          {m.assigned_co_name && (
            <p className="text-xs text-slate-500 mt-4">Assigned CO: <span className="font-medium text-slate-700">{m.assigned_co_name}</span></p>
          )}
          {m.created_at && (
            <p className="text-xs text-slate-400 mt-1">Registered: {formatDate(m.created_at)}</p>
          )}
        </CardContent>
      </Card>

      {/* ── Section 2: CO Recommendation ── */}
      {m.kyc_last_co_comments && (
        <Card className="border-[#DCE4E0] shadow-sm border-l-4 border-l-[#0B6445]">
          <CardContent className="py-5 px-6">
            <p className="text-sm font-semibold text-[#073B2E] flex items-center gap-2 mb-3">
              <MessageSquare className="w-4 h-4" /> CO Recommendation
            </p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{m.kyc_last_co_comments}</p>
          </CardContent>
        </Card>
      )}

      {/* ── Section 3: Business & Contact Information ── */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border-[#DCE4E0] shadow-sm">
          <CardContent className="py-5 px-6">
            <p className="text-sm font-semibold text-[#073B2E] flex items-center gap-2 mb-4">
              <Building2 className="w-4 h-4" /> Business Information
            </p>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ['Business / Entity Name', company.entity_name || biz.EntityName || biz.entity_name || biz.business_name || company.business_name],
                ['ABN', company.abn || biz.Abn || biz.abn],
                ['ACN', company.acn || biz.Acn || biz.acn],
                ['Entity / Business Type', company.entity_type || ob.entity_type || biz.EntityType || biz.entityType || biz.business_type || company.business_type],
                ['Industry', ob.industry || biz.industry || company.industry],
                ['Website', ob.website || biz.website || company.website],
                ['Phone', ob.phone || biz.phone || company.phone],
                ['State', company.state || biz.State || biz.state],
                ['Postcode', company.postcode || biz.Postcode || biz.postcode],
                ['Location', company.location || biz.Location || biz.location],
                ['Business Address', company.business_address || biz.BusinessAddress || biz.business_address],
              ].map(([label, val]) => val ? (
                <React.Fragment key={label}>
                  <dt className="text-slate-500">{label}</dt>
                  <dd className="font-medium text-slate-800 truncate">{val}</dd>
                </React.Fragment>
              ) : null)}
            </dl>
            {!company.entity_name && !company.abn && !biz.EntityName && !biz.Abn && !biz.business_name && !company.business_name && (
              <p className="text-xs text-slate-400 italic">No business information submitted.</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardContent className="py-5 px-6">
            <p className="text-sm font-semibold text-[#073B2E] flex items-center gap-2 mb-4">
              <User className="w-4 h-4" /> Contact Information
            </p>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ['Full Name', ob.personal?.full_name || m.name],
                ['Email', ob.personal?.email || m.email],
                ['Phone', ob.personal?.phone],
                ['Date of Birth', ob.personal?.dob],
                ['Address', ob.personal?.address],
                ['City', ob.personal?.city],
                ['Postcode', ob.personal?.postcode],
              ].map(([label, val]) => val ? (
                <React.Fragment key={label}>
                  <dt className="text-slate-500">{label}</dt>
                  <dd className="font-medium text-slate-800 truncate">{val}</dd>
                </React.Fragment>
              ) : null)}
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* ── Section 4: Supporting Documents ── */}
      {docs.length > 0 && (
        <Card className="border-[#DCE4E0] shadow-sm">
          <CardContent className="py-5 px-6">
            <p className="text-sm font-semibold text-[#073B2E] flex items-center gap-2 mb-4">
              <FileText className="w-4 h-4" /> Supporting Documents ({docs.length})
            </p>
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {docs.map((doc, i) => {
                const docKey = doc.type || doc.doc_type || doc.file_name?.replace(/\.[^.]+$/, '').replace(/\s+/g, '_').toLowerCase() || `doc_${i}`;
                const reviewStatus = docReviews[docKey];
                return (
                  <div key={i} className="border border-slate-200 rounded-lg p-4 space-y-3 bg-slate-50/30">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold text-slate-700">{getDocLabel(doc)}</h4>
                      {reviewStatus && (
                        <Badge variant="outline" className={`text-[10px] ${reviewStatus === 'accepted' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                          {reviewStatus === 'accepted' ? 'Accepted' : 'Update Req.'}
                        </Badge>
                      )}
                    </div>
                    <DocPreview doc={doc} />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Additional information (info requests + merchant replies + response docs) ── */}
      {infoReqs.length > 0 && (
        <Card className="border-[#DCE4E0] shadow-sm">
          <CardContent className="py-5 px-6">
            <p className="text-sm font-semibold text-[#073B2E] flex items-center gap-2 mb-4">
              <MessageSquare className="w-4 h-4" /> Additional information ({infoReqs.length} request{infoReqs.length !== 1 ? 's' : ''})
            </p>
            <div className="space-y-8">
              {infoReqs.map((req) => {
                const respTypes = (req.response_document_types || []).map((t) => (t || '').toLowerCase());
                const responseDocs = respTypes.length
                  ? docs.filter((d) => {
                      const t = (d.type || d.category || '').toLowerCase();
                      return t && respTypes.includes(t);
                    })
                  : [];
                const isResponded = req.status === 'responded';
                const isExpanded = isResponded ? (expandedInfoReqs[req.id] === true) : true;
                return (
                  <div key={req.id} className="rounded-lg border border-slate-200 bg-slate-50/40 overflow-hidden">
                    <button
                      type="button"
                      className={`w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors ${isResponded ? 'cursor-pointer hover:bg-slate-50/50' : 'cursor-default'}`}
                      onClick={() => isResponded && setExpandedInfoReqs((p) => ({ ...p, [req.id]: !p[req.id] }))}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Badge variant="outline" className={`shrink-0 ${isResponded ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                          {isResponded ? 'Responded' : 'Open'}
                        </Badge>
                        <span className="text-sm text-slate-700 truncate">{req.message}</span>
                      </div>
                      <span className="text-xs text-slate-400 shrink-0">{formatDate(req.created_at)}</span>
                      {isResponded && (
                        <span className="shrink-0 text-slate-400">
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </span>
                      )}
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-0 space-y-3 border-t border-slate-200/60">
                        {req.requested_by_name && (
                          <p className="text-xs text-slate-500 pt-3">Requested by: {req.requested_by_name}</p>
                        )}
                        <p className="text-sm font-medium text-slate-800">{req.message}</p>
                        {req.status === 'responded' && req.merchant_response && (
                          <div className="rounded-lg bg-emerald-50/80 border border-emerald-200 p-3">
                            <p className="text-xs font-medium text-emerald-700 mb-1">Merchant response:</p>
                            <p className="text-sm text-emerald-800">{req.merchant_response}</p>
                          </div>
                        )}
                        {req.status === 'responded' && responseDocs.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-slate-600">Documents attached:</p>
                            <div className="space-y-2">
                              {responseDocs.map((doc, idx) => (
                                <div key={idx} className="flex items-center justify-between py-2 px-3 rounded-lg border border-[#DCE4E0] bg-white">
                                  <span className="text-sm text-slate-700 truncate">{doc.name || doc.filename || getDocLabel(doc) || `Document ${idx + 1}`}</span>
                                  <Button type="button" size="sm" variant="outline" className="shrink-0 ml-2 border-[#0B6445] text-[#0B6445] hover:bg-[#E6F7F3]" onClick={() => setViewingDoc(doc)}>
                                    <Eye className="w-3.5 h-3.5 mr-1" /> View
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Section 5: Review History (collapsible) ── */}
      {reviews.length > 0 && (
        <CollapsibleSection icon={Calendar} title="Review History" count={reviews.length}>
          <div className="pt-3">
            {reviews.map((r, i) => (
              <div key={r.id || i} className="flex gap-3 py-3 border-b border-slate-100 last:border-0">
                <div className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${
                  r.recommendation === 'approve' || r.action_type === 'approved' ? 'bg-green-500' :
                  r.recommendation === 'reject' || r.action_type === 'rejected' ? 'bg-red-500' :
                  r.action_type === 'document_accepted' ? 'bg-emerald-400' :
                  r.action_type === 'document_update_requested' ? 'bg-amber-400' : 'bg-blue-400'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-slate-700">
                      {REVIEW_ACTION_LABELS[r.action_type] || REVIEW_ACTION_LABELS[r.recommendation] || r.action_type || 'Review'}
                    </span>
                    {r.reviewer_name && <span className="text-xs text-slate-400">by {r.reviewer_name}</span>}
                  </div>
                  {r.comments && <p className="text-xs text-slate-600 mt-1 line-clamp-3">{r.comments}</p>}
                  {r.doc_type && <p className="text-xs text-slate-400 mt-0.5">Document: {getDocLabel({ type: r.doc_type })}</p>}
                  <p className="text-[11px] text-slate-400 mt-1">{formatDate(r.created_at)}</p>
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ── Section 6: Information Requests (collapsible) ── */}
      {infoReqs.length > 0 && (
        <CollapsibleSection icon={Eye} title="Information Requests" count={infoReqs.length}>
          <div className="pt-3 space-y-6">
            {infoReqs.map((req, i) => (
              <div key={req.id || i} className="border border-slate-200 rounded-lg p-4 bg-slate-50/30">
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className={`text-[10px] ${req.status === 'responded' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                    {req.status === 'responded' ? 'Responded' : 'Open'}
                  </Badge>
                  <span className="text-[11px] text-slate-400">{formatDate(req.created_at)}</span>
                </div>
                {req.message && <p className="text-xs text-slate-600 mt-1">{req.message}</p>}
                {req.missing_items?.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1.5">Requested items: {req.missing_items.join(', ')}</p>
                )}
                {req.requested_by_name && (
                  <p className="text-xs text-slate-400 mt-1.5">Requested by: {req.requested_by_name}</p>
                )}
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* ── Bottom Action Buttons ── */}
      {showApproveReject && (
        <div className="flex justify-end gap-4 pt-6 pb-6 border-t border-[#DCE4E0] mt-2">
          <Button size="lg" onClick={() => { setActionType('approve'); setSuComments(''); }}
            className="bg-[#0B6445] hover:bg-[#095b3d] text-white px-8">
            <CheckCircle className="w-4 h-4 mr-2" /> Approve Merchant
          </Button>
          <Button size="lg" variant="outline" onClick={() => { setActionType('reject'); setSuComments(''); }}
            className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700 px-8">
            <XCircle className="w-4 h-4 mr-2" /> Reject Merchant
          </Button>
        </div>
      )}

      {/* Document viewer for Additional information response docs */}
      {viewingDoc && (
        <Dialog open={!!viewingDoc} onOpenChange={(open) => { if (!open) setViewingDoc(null); }}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-[#0F172A]">{viewingDoc.name || viewingDoc.filename || 'Document'}</DialogTitle>
            </DialogHeader>
            <div className="mt-2">
              <DocPreview doc={viewingDoc} />
            </div>
            <div className="flex justify-end pt-3">
              <Button variant="outline" size="sm" onClick={() => setViewingDoc(null)}>Close</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Approve/Reject Confirmation Dialog */}
      <Dialog open={!!actionType} onOpenChange={() => { setActionType(null); setSuComments(''); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">{actionType === 'approve' ? 'Approve KYC' : 'Reject KYC'}</DialogTitle>
            <DialogDescription className="text-slate-600">
              {actionType === 'approve'
                ? `Approve KYC for ${m.name || m.email}? This grants them full Live access.`
                : `Reject KYC for ${m.name || m.email}?`}
            </DialogDescription>
          </DialogHeader>
          {m.kyc_last_co_comments && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <p className="text-xs font-medium text-slate-500 mb-1">CO Recommendation:</p>
              <p className="text-sm text-slate-700">{m.kyc_last_co_comments}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-1.5">
              Your Comments <span className="text-red-500">*</span>
            </label>
            <Textarea placeholder={actionType === 'approve' ? 'Approval rationale…' : 'Rejection reason…'} value={suComments} onChange={(e) => setSuComments(e.target.value)} className="min-h-[80px]" />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setActionType(null)} className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">Cancel</Button>
            <Button onClick={handleAction} disabled={actionLoading || !suComments.trim()} className={actionType === 'approve' ? 'bg-[#0B6445] hover:bg-[#095b3d] text-white' : 'bg-red-600 hover:bg-red-700 text-white'}>
              {actionLoading ? '…' : actionType === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function BackOfficeKycPending() {
  const { api, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLive } = useEnvironment();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMerchant, setSelectedMerchant] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.get('/backoffice/merchants?kyc_status=co_recommended_approve&per_page=100'),
        api.get('/backoffice/merchants?kyc_status=co_recommended_reject&per_page=100'),
        api.get('/backoffice/merchants?kyc_status=in_review&per_page=100'),
        api.get('/backoffice/merchants?kyc_status=info_requested&per_page=100'),
      ]);
      const all = [];
      for (const r of results) {
        if (r.status === 'fulfilled') all.push(...(r.value.data?.data || []));
      }
      const seen = new Set();
      const deduped = all.filter(m => { if (seen.has(m.id)) return false; seen.add(m.id); return true; });
      deduped.sort((a, b) => {
        const order = { co_recommended_approve: 0, co_recommended_reject: 1, in_review: 2, info_requested: 3 };
        return (order[a.kyc_status] ?? 9) - (order[b.kyc_status] ?? 9);
      });
      setData(deduped);
    } catch { setData([]); } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData, isLive]);

  const merchantIdFromUrl = searchParams.get('merchant_id');
  useEffect(() => {
    if (!merchantIdFromUrl || loading || data.length === 0) return;
    const row = data.find((m) => m.id === merchantIdFromUrl);
    if (row && !selectedMerchant) {
      setSelectedMerchant(row);
      setSearchParams({}, { replace: true });
    }
  }, [merchantIdFromUrl, loading, data, selectedMerchant, setSearchParams]);

  if (selectedMerchant) {
    return (
      <BackOfficeLayout>
        <MerchantDetail
          merchant={selectedMerchant}
          api={api}
          currentUser={user}
          onBack={() => setSelectedMerchant(null)}
          onActionComplete={() => { setSelectedMerchant(null); fetchData(); }}
        />
      </BackOfficeLayout>
    );
  }

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2"><Clock className="w-7 h-7 text-[#0B6445]" />Pending Review</h1>
            <p className="text-[#0E1B16]/70 mt-2">KYC/KYB applications awaiting your final decision. Click a merchant to review.</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchData} className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
        </div>
        <Card className="border-[#DCE4E0] shadow-sm">
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
            ) : data.length === 0 ? (
              <p className="text-center py-12 text-[#0E1B16]/60">No pending applications.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#DCE4E0]">
                    <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Merchant</th>
                    <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Email</th>
                    <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Status</th>
                    <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">CO Comments</th>
                    <th className="text-right py-3 px-2 font-semibold text-[#073B2E]">Actions</th>
                  </tr></thead>
                  <tbody>
                    {data.map((row) => (
                      <tr key={row.id}
                        className="border-b border-[#DCE4E0]/80 hover:bg-[#E6F7F3]/40 cursor-pointer transition-colors"
                        onClick={() => setSelectedMerchant(row)}
                      >
                        <td className="py-3 px-2">
                          <div className="font-medium text-[#0B6445] hover:underline">{row.name || '—'}</div>
                          {row.company_name && <div className="text-xs text-slate-400">{row.company_name}</div>}
                        </td>
                        <td className="py-3 px-2">{row.email}</td>
                        <td className="py-3 px-2">
                          <Badge variant="outline" className={STATUS_BADGE[row.kyc_status] || STATUS_BADGE.pending}>
                            {row.kyc_status === 'co_recommended_approve' && <CheckCircle className="w-3 h-3 mr-1" />}
                            {row.kyc_status === 'co_recommended_reject' && <XCircle className="w-3 h-3 mr-1" />}
                            {STATUS_LABELS[row.kyc_status] || row.kyc_status}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 max-w-[200px]">
                          <p className="text-xs text-slate-500 truncate">{row.kyc_last_co_comments || '—'}</p>
                        </td>
                        <td className="py-3 px-2 text-right space-x-2" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" onClick={() => setSelectedMerchant(row)} className="bg-[#0B6445] hover:bg-[#095b3d] text-white">
                            <Eye className="w-3.5 h-3.5 mr-1" /> Review
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
}
